// @ts-strict-ignore
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router'
import { DateErrorType, toast } from '@node-space/ui-components'
import { UKRAINE, UNITED_STATES } from 'constants/contacts'
import { PathNames } from 'constants/General'
import { dateInitialErrors, useDateInput } from 'hooks/forms/useDateInput'
import { useFormFields } from 'hooks/forms/useFormFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { usePostContactMutation } from 'hooks/mutations/usePostContact'
import { usePutContactMutation } from 'hooks/mutations/usePutContactMutation'
import { useCountryStatesQuery } from 'hooks/queries/Customers/useCountryStates'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  ContactEntity,
  ContactInstrumentDecisionStep,
  ContactPayload,
  InstrumentType,
  PutContactProps,
  RelationshipType,
  type Contact,
  type ContactEntityType,
} from 'types/contacts'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentDecisionStepAtom,
  editingContactAtom,
} from '../atoms/contactAtoms'
import { ContactFormData, ContactFormKeys, contactSchema } from '../schemas/contactSchema'
import { FIAT_SECTION_FIELDS, ukraineRegionOptions } from '../utils/constants'
import { withRequiredGovernmentId } from '../utils/governmentIds'
import { getContactDefaultValues, handleContactOnError } from '../utils/utils'
import { useGovernmentIds } from './useGovernmentIds'

interface UseContactFormProps {
  requiredFieldsForContactUpdate?: ContactFormKeys[]
  requiredGovernmentIdCountry?: string
  setDateErrors: Dispatch<SetStateAction<DateErrorType>>
}

export const useContactForm = ({
  requiredFieldsForContactUpdate,
  requiredGovernmentIdCountry,
  setDateErrors,
}: UseContactFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const match = useMatch(PathNames.CONTACTS)
  const { enableGovernmentIds } = useFeatureFlags()

  const setContactInstrumentDecisionStep = useSetAtom(contactInstrumentDecisionStepAtom)
  const [contactsAction] = useAtom(contactActionAtom)
  const { flowType } = contactsAction?.meta || {}
  const [contact, setContact] = useAtom(contactAtom)
  const setEditingContact = useSetAtom(editingContactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const { formatDateInput, formatDateData } = useDateInput()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { mutate: postContact, isPending: isPostingContact } = usePostContactMutation()
  const { mutate: updateContact, isPending: isUpdatingContact } = usePutContactMutation()
  const { data: countries, isPending: isCountriesLoading } = useFilteredCountriesQuery(true)

  const isCryptoFlow = useMemo(() => {
    return (
      flowType === InstrumentType.CRYPTO ||
      contactInstrumentAction?.meta?.flowType === InstrumentType.CRYPTO ||
      contactInstrumentAction?.meta?.contactInstrument?.type === InstrumentType.CRYPTO
    )
  }, [flowType, contactInstrumentAction])

  const countriesOptions = countries?.map(country => ({
    leftText: country?.name,
    value: country?.code,
  }))

  const formSchema = contactSchema({
    t,
    requiredFields: requiredFieldsForContactUpdate,
    requiredGovernmentIdCountry,
    isCryptoFlow,
  })

  const isEditingContact = !isEmpty(contact)

  useEffect(() => {
    setEditingContact(isEditingContact)
  }, [isEditingContact, setEditingContact])

  const defaultValues = getContactDefaultValues(
    isEditingContact,
    contact,
    formatDateData,
    enableGovernmentIds
  )

  const form = useForm<ContactFormData>({
    defaultValues: {
      ...defaultValues,
      governmentIds: withRequiredGovernmentId(
        defaultValues.governmentIds,
        requiredGovernmentIdCountry,
        defaultValues.entityType
      ),
    },
    resolver: yupFormResolver<ContactFormData>(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const {
    governmentIds,
    onAddGovernmentId,
    onChangeGovernmentId,
    onRemoveGovernmentId,
    onResetGovernmentIds,
    onChangeGovernmentIdsEntityType,
  } = useGovernmentIds(form)

  const isCompany = formValues?.entityType === ContactEntity.COMPANY
  const isIndividual = formValues?.entityType === ContactEntity.INDIVIDUAL
  const isUkraineSelected =
    (isIndividual && formValues?.countryOfResidence === UKRAINE) ||
    (isCompany && formValues?.companyRegistrationCountry === UKRAINE)

  const isUSSelected =
    (isIndividual && formValues?.countryOfResidence === UNITED_STATES) ||
    (isCompany && formValues?.companyRegistrationCountry === UNITED_STATES)

  const { data: usStates, isPending: isStatesLoading } = useCountryStatesQuery(
    UNITED_STATES,
    isUSSelected
  )

  useEffect(() => {
    if (isEditingContact && isUSSelected && usStates?.length && !formValues?.regionCode) {
      const regionValue = formValues?.region
      if (regionValue) {
        const matchedState = usStates.find(
          state =>
            (state.leftText as string)?.toLowerCase() === regionValue.toLowerCase() ||
            state.value?.toLowerCase() === regionValue.toLowerCase()
        )
        if (matchedState) {
          setValue('regionCode', matchedState.value, { shouldValidate: true })
        }
      }
    }
  }, [
    isEditingContact,
    isUSSelected,
    usStates,
    formValues?.regionCode,
    formValues?.region,
    setValue,
  ])

  const { setRadioField, setDatePickerField, setInputField, setCheckboxField, setComboboxField } =
    useFormFields<ContactFormKeys>(formSchema, form, formValues)

  const onEntityTypeChange = (newEntityType: string) => {
    setDateErrors(dateInitialErrors)
    setValue('dateOfBirth', '')
    clearErrors()
    setRequestError({
      show: false,
    })
    onChangeGovernmentIdsEntityType(newEntityType as ContactEntityType)
  }

  const formFields: { [key in ContactFormKeys] } = {
    entityType: setRadioField({
      key: 'entityType',
      options: [
        { value: ContactEntity.COMPANY, label: t('contacts.company') },
        { value: ContactEntity.INDIVIDUAL, label: t('contacts.individual') },
      ],
      onChangeAdditional: onEntityTypeChange,
    }),
    companyName: setInputField({
      key: 'companyName',
      placeholder: t('formFields.placeholder.companyName'),
    }),
    contactDescription: setInputField({
      key: 'contactDescription',
      placeholder: isCompany
        ? t('formFields.placeholder.contactCompanyDescription')
        : t('formFields.placeholder.contactPersonDescription'),
    }),
    companyRegistrationNumber: setInputField({
      key: 'companyRegistrationNumber',
      placeholder: t('formFields.companyRegistrationNumber'),
    }),
    companyRegistrationCountry: setComboboxField({
      key: 'companyRegistrationCountry',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('companyRegistrationCountry'),
    }),
    addressLine1: setInputField({
      key: 'addressLine1',
      placeholder: t('formFields.addressLine1'),
    }),
    addressLine2: setInputField({
      key: 'addressLine2',
      placeholder: t('formFields.addressLine2'),
    }),
    city: setInputField({
      key: 'city',
      placeholder: t('formFields.city'),
    }),
    postCode: setInputField({
      key: 'postCode',
      placeholder: t('formFields.postCode'),
    }),
    region: setInputField({
      key: 'region',
      placeholder: t('formFields.region'),
    }),
    regionCode: setComboboxField({
      key: 'regionCode',
      items: isUSSelected ? (usStates ?? []) : ukraineRegionOptions,
      placeholder: t('select'),
      defaultValue: watch('regionCode'),
    }),
    firstName: setInputField({
      key: 'firstName',
      placeholder: t('formFields.placeholder.firstNameAndMiddleNames'),
    }),
    lastName: setInputField({
      key: 'lastName',
      placeholder: t('formFields.placeholder.lastName'),
    }),
    countryOfResidence: setComboboxField({
      key: 'countryOfResidence',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('countryOfResidence'),
    }),
    isSelfOwned: setCheckboxField({
      key: 'isSelfOwned',
      name: 'isSelfOwned',
      id: 'isSelfOwned',
      testId: 'is-self-owned',
    }),
    dateOfBirth: setDatePickerField({ key: 'dateOfBirth' }),
    governmentIds: {},
  }

  const getRelationshipType = () => {
    if (isIndividual) {
      return RelationshipType.THIRD_PARTY
    }

    return formValues?.isSelfOwned ? RelationshipType.SELF_OWNED : RelationshipType.THIRD_PARTY
  }

  const getRegionValue = (regionCode: string, region: string): string => {
    if (isUSSelected) {
      return (
        (usStates?.find(state => state.value === regionCode)?.leftText as string) || region || ''
      )
    }
    if (isUkraineSelected) {
      return regionCode
    }
    return region
  }

  const buildContactPayload = (): ContactPayload => {
    const {
      firstName,
      lastName,
      companyName,
      contactDescription,
      addressLine1,
      addressLine2,
      city,
      postCode,
      region,
      regionCode,
      countryOfResidence,
      companyRegistrationNumber,
      companyRegistrationCountry,
      dateOfBirth,
    } = formValues || {}

    const isGovernmentIdsRequired = requiredFieldsForContactUpdate?.includes('governmentIds')

    const hasAddress =
      !!addressLine1 || !!addressLine2 || !!city || !!postCode || !!region || !!regionCode

    return {
      description: contactDescription,
      relationshipType: getRelationshipType(),
      entity: {
        ...(isCompany && {
          legalName: companyName,
          ...(companyRegistrationNumber && {
            registrationNumber: formValues?.companyRegistrationNumber,
          }),
        }),
        ...(isIndividual && {
          firstName: firstName,
          lastName: lastName,
          ...(dateOfBirth && { dateOfBirth: formatDateInput(dateOfBirth) }),
        }),
        ...(hasAddress && {
          address: {
            region: getRegionValue(regionCode, region),
            ...(addressLine1 && { addressLine1: addressLine1 }),
            ...(addressLine2 && { addressLine2: addressLine2 }),
            ...(city && { city: city }),
            ...(postCode && { postCode: postCode }),
            ...(isIndividual && countryOfResidence && { country: countryOfResidence }),
            ...(isCompany && companyRegistrationCountry && { country: companyRegistrationCountry }),
            ...(isUSSelected && regionCode && { stateCode: regionCode }),
          },
        }),
        ...((enableGovernmentIds || isGovernmentIdsRequired) &&
          governmentIds.length > 0 && {
            governmentIds: governmentIds.map(id => ({
              type: id.type,
              number: id.number.trim(),
              issuingCountry: id.issuingCountry,
              ...(id.issueDate && { issueDate: id.issueDate }),
              ...(id.expiryDate && { expiryDate: id.expiryDate }),
            })),
          }),
        type: formValues?.entityType,
      },
    }
  }

  const handlePostContactSuccess = (response: Contact) => {
    toast({
      header: t('contacts.addContactSuccess', {
        contactName: response?.name,
      }),
      variant: 'success',
    })
    setContact(response)

    if (
      contactInstrumentAction?.actionId === ContactInstrumentActionIds?.CONTACT_INSTRUMENT_DECISION
    ) {
      setContactInstrumentDecisionStep(ContactInstrumentDecisionStep.INSTRUMENT)
    } else if (contactsAction?.meta?.enableCreateInstrument) {
      setContactInstrumentDecisionStep(ContactInstrumentDecisionStep.INSTRUMENT)
      setContactInstrumentAction({
        ...contactInstrumentAction,
        header: t('contacts.addBankAccountOrCryptoAddress'),
        actionId: ContactInstrumentActionIds.CONTACT_INSTRUMENT_DECISION,
        onClose: () => {
          contactsAction?.onClose()
          setContact(null)
          setContactInstrumentAction(null)
          queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
        },
      })
    } else if (match) {
      void navigate(`${PathNames.CONTACTS}/${response?.id}`)
    } else {
      setContact(null)
      contactsAction?.onClose()
      queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
    }
    queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
    queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS_V3] })
    queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS_V3_INFINITE] })
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const data = buildContactPayload()

    if (isEditingContact) {
      const payload: PutContactProps = {
        contact: data,
        contactId: contact?.id,
      }
      updateContact(payload, {
        onSuccess: response => {
          toast({
            header: t('contacts.updateContactSuccess', {
              contactName: response?.name,
            }),
            variant: 'success',
          })
          setContact(contactsAction?.meta?.keepUpdatedContact ? response : null)
          contactsAction?.onClose()
        },
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePutContactMutation', t)
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS_V3] })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS_V3_INFINITE] })
          queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
        },
      })
    } else {
      postContact(data, {
        onSuccess: handlePostContactSuccess,
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePostContactMutation', t)
        },
      })
    }
  }

  const onChangeCountry = (
    key: 'countryOfResidence' | 'companyRegistrationCountry',
    value: string
  ) => {
    setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
    if (value !== UKRAINE && value !== UNITED_STATES) {
      trigger('regionCode')
    }
  }

  const onResetFiatSection = () => {
    FIAT_SECTION_FIELDS.forEach(field => setValue(field, ''))
    setDateErrors(dateInitialErrors)
    clearErrors(FIAT_SECTION_FIELDS)
  }

  return {
    formFields,
    formValues,
    governmentIds,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    requestError,
    isEditingContact,
    isUpdatingContact,
    isUkraineSelected,
    isUSSelected,
    isStatesLoading,
    isCryptoFlow,
    handleSubmit,
    onSubmit,
    setValue,
    onChangeCountry,
    onAddGovernmentId,
    onChangeGovernmentId,
    onRemoveGovernmentId,
    onResetFiatSection,
    onResetGovernmentIds,
    trigger,
  }
}
